import React from 'react';
import { ServicePackage } from '../../types/service';
import { 
  CheckIcon, ClockIcon, ArrowPathIcon, UsersIcon, 
  MapPinIcon, VideoCameraIcon, PhotoIcon, StarIcon 
} from '@heroicons/react/24/outline';
import Button from '../common/Button';
import { formatCurrency } from '../../utils/format';
import { resolveImageUrl } from '../../utils/image';

interface ServicePackageCardProps {
  packageData: ServicePackage;
  isOwner: boolean;
  onEdit?: (pkg: ServicePackage) => void;
  onDelete?: (pkg: ServicePackage) => void;
  onBook?: (pkg: ServicePackage) => void;
  onClick?: (pkg: ServicePackage) => void;
}

const ServicePackageCard: React.FC<ServicePackageCardProps> = ({
  packageData,
  isOwner,
  onEdit,
  onDelete,
  onBook,
  onClick,
}) => {
  const coverImage = packageData.sample_images_signed?.[0] || packageData.sample_images?.[0];

  return (
    <div 
      onClick={() => onClick?.(packageData)}
      className="bg-white dark:bg-gray-900 rounded-[24px] border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl hover:border-orange-200 dark:hover:border-orange-900/50 transition-all duration-300 flex flex-col h-full relative cursor-pointer overflow-hidden group"
    >
      {/* Premium Cover Banner */}
      <div className="w-full h-52 overflow-hidden shrink-0 relative bg-gray-100 dark:bg-gray-800">
        {coverImage ? (
          <img 
            src={resolveImageUrl(coverImage)} 
            alt={packageData.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-700">
             <PhotoIcon className="w-12 h-12" />
          </div>
        )}
        
        {/* Gradients */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Floating Badges */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          <span className="inline-block px-3 py-1.5 rounded-xl text-[10px] font-black bg-white/90 dark:bg-black/80 backdrop-blur-md text-gray-900 dark:text-white uppercase tracking-wider shadow-sm border border-white/20">
            {packageData.category || 'Package'}
          </span>
          <span className="bg-orange-500 text-white px-3.5 py-1.5 rounded-xl text-sm font-black tracking-wide shadow-lg border border-orange-400">
             {packageData.price === 0 ? 'Free' : formatCurrency(packageData.price)}
          </span>
        </div>

        {/* Floating Title (inside image) */}
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl font-bold text-white leading-tight drop-shadow-md line-clamp-2" title={packageData.title}>
            {packageData.title}
          </h3>
        </div>
      </div>

      <div className="p-5 flex-1 flex flex-col pt-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 flex-grow line-clamp-2 leading-relaxed">
          {packageData.description}
        </p>

        {/* Specs Grid */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center gap-2 text-[11px] font-bold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 px-3 py-2.5 rounded-xl border border-gray-100 dark:border-gray-800 uppercase tracking-wide">
            <ClockIcon className="h-4 w-4 text-orange-500 shrink-0" />
            <span className="truncate">{packageData.session_duration_hours} Hrs / {packageData.number_of_days} Days</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-bold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 px-3 py-2.5 rounded-xl border border-gray-100 dark:border-gray-800 uppercase tracking-wide">
            <UsersIcon className="h-4 w-4 text-purple-500 shrink-0" />
            <span className="truncate">{packageData.team_size} Person(s)</span>
          </div>
        </div>

        {/* Feature Chips */}
        <div className="flex flex-wrap gap-2 mb-5">
          {packageData.number_of_edited_photos > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/20 text-[10px] font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider">
               <PhotoIcon className="w-3 h-3 mr-1" /> {packageData.number_of_edited_photos} Edits
            </span>
          )}
          {packageData.video_included && (
            <span className="inline-flex items-center px-2 py-1 rounded bg-red-50 dark:bg-red-900/20 text-[10px] font-bold text-red-700 dark:text-red-300 uppercase tracking-wider">
               <VideoCameraIcon className="w-3 h-3 mr-1" /> Video
            </span>
          )}
          {packageData.travel_included && (
            <span className="inline-flex items-center px-2 py-1 rounded bg-stone-100 dark:bg-stone-800 text-[10px] font-bold text-stone-700 dark:text-stone-300 uppercase tracking-wider">
               🚗 Travel
            </span>
          )}
          {packageData.revisions > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded bg-green-50 dark:bg-green-900/20 text-[10px] font-bold text-green-700 dark:text-green-300 uppercase tracking-wider">
               <ArrowPathIcon className="w-3 h-3 mr-1" /> {packageData.revisions} Rev
            </span>
          )}
          {packageData.portfolio_folders && packageData.portfolio_folders.length > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded bg-orange-50 dark:bg-orange-900/20 text-[10px] font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider">
               📁 {packageData.portfolio_folders.length} Folders
            </span>
          )}
        </div>

        {/* Inclusions summary (top 2) */}
        {(packageData.inclusions?.length > 0 || packageData.perks?.length > 0) && (
          <div className="border-t border-gray-100 dark:border-gray-800 pt-4 mb-5">
             {packageData.inclusions?.slice(0, 2).map((inc, i) => (
                <div key={i} className="flex items-start text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <CheckIcon className="h-4 w-4 text-green-500 mr-2 shrink-0 mt-0.5" />
                  <span className="truncate">{inc}</span>
                </div>
             ))}
             {packageData.inclusions?.length === 0 && packageData.perks?.slice(0, 2).map((perk, i) => (
                <div key={i} className="flex items-start text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <StarIcon className="h-4 w-4 text-yellow-500 mr-2 shrink-0 mt-0.5" />
                  <span className="truncate">{perk}</span>
                </div>
             ))}
             {((packageData.inclusions?.length || 0) + (packageData.perks?.length || 0) > 2) && (
                <div className="text-[11px] text-gray-400 font-bold ml-6 uppercase">
                   + {(packageData.inclusions?.length || 0) + (packageData.perks?.length || 0) - 2} more
                </div>
             )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto" onClick={(e) => e.stopPropagation()}>
          {isOwner ? (
            <div className="flex space-x-2">
              <button
                onClick={() => onEdit?.(packageData)}
                className="flex-1 rounded-xl py-2.5 border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black hover:bg-transparent dark:hover:bg-transparent hover:text-black dark:hover:text-white font-bold text-sm transition-all"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete?.(packageData)}
                className="flex-1 rounded-xl py-2.5 border-2 border-red-200 dark:border-red-900 text-red-500 hover:bg-orange-500 hover:border-orange-500 hover:text-white dark:hover:bg-orange-500 dark:hover:border-orange-500 font-bold text-sm transition-all"
              >
                Delete
              </button>
            </div>
          ) : (
            <Button
              variant="primary"
              className="w-full rounded-xl py-3 font-bold tracking-wide transition-all bg-orange-500 hover:bg-orange-600 text-white shadow-xl shadow-orange-500/20"
              onClick={() => onClick?.(packageData)}
            >
              View Details
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicePackageCard;
